import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  CircleAlert,
  FileText,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';

import { triggerDjenIngest } from '../../api/client';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/spinner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import type { DjenProgress } from '../../hooks/useDjenProgress';
import { formatOabLabel } from './helpers';

// ---------------------------------------------------------------------------
// Empty state: INGESTING (DJEN em andamento ou pendente)
// ---------------------------------------------------------------------------
export function IngestingState({
  user,
  djenProgress,
  mode,
}: {
  user: { nome?: string; oabs?: Array<{ numero: string; uf: string }>; identidade_verificada?: boolean } | null;
  djenProgress: DjenProgress | null;
  mode: 'indeterminate' | 'determinate';
}) {
  const navigate = useNavigate();
  const oab = user?.oabs?.[0];
  const pct = djenProgress?.pct ?? 0;
  const totalIntimacoes = djenProgress?.total_comunicacoes ?? djenProgress?.comunicacoes ?? 0;
  const totalProcessosDjen = djenProgress?.processos ?? 0;
  const hasCertificate = user?.identidade_verificada ?? false;

  const title = mode === 'indeterminate'
    ? 'Preparando tudo para você...'
    : 'Buscando suas intimações...';

  const description = mode === 'indeterminate'
    ? 'Estamos configurando o monitoramento do Diário da Justiça Eletrônico para a sua OAB. Isso pode levar alguns minutos.'
    : 'Estamos consultando o Diário da Justiça Eletrônico' + formatOabLabel(oab) + '.';

  const actionCards = hasCertificate
    ? [
        { label: 'Explorar modelos de petição', icon: FileText, color: 'text-blue-500', route: '/app/modelos' },
        { label: 'Acessar agenda', icon: CalendarIcon, color: 'text-amber-500', route: '/app/calendario' },
        { label: 'Personalizar o sistema', icon: Settings, color: 'text-muted-foreground', route: '/app/configuracoes' },
      ]
    : [
        { label: 'Cadastrar certificado digital', icon: ShieldCheck, color: 'text-emerald-500', route: '/app/certificados' },
        { label: 'Explorar modelos de petição', icon: FileText, color: 'text-blue-500', route: '/app/modelos' },
        { label: 'Personalizar o sistema', icon: Settings, color: 'text-muted-foreground', route: '/app/configuracoes' },
      ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Spinner size={32} />
            </div>
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {mode === 'determinate' ? (
            <>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Progresso</span>
                  <span className="font-medium">{pct}%</span>
                </div>
                <div className="h-2.5 w-full rounded-full bg-secondary">
                  <div
                    className="h-2.5 rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              {(totalIntimacoes > 0 || totalProcessosDjen > 0) && (
                <p className="text-sm text-muted-foreground">
                  {totalIntimacoes} {totalIntimacoes === 1 ? 'intimação encontrada' : 'intimações encontradas'} em {totalProcessosDjen} {totalProcessosDjen === 1 ? 'processo' : 'processos'}
                </p>
              )}
            </>
          ) : (
            <div className="h-2.5 w-full rounded-full bg-secondary overflow-hidden">
              <div className="h-2.5 w-1/3 rounded-full bg-primary animate-dash-indeterminate" />
            </div>
          )}
        </CardContent>
      </Card>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Enquanto isso, você pode:
        </h3>
        <div className="grid gap-3 sm:grid-cols-3">
          {actionCards.map((card) => (
            <Card
              key={card.route}
              className="cursor-pointer transition-colors hover:border-primary/40"
              onClick={() => navigate(card.route)}
            >
              <CardContent className="flex flex-col items-center gap-2 p-5 pt-5">
                <card.icon className={`h-8 w-8 ${card.color}`} />
                <span className="text-sm font-medium">{card.label}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state: EMPTY_WITH_OAB
// ---------------------------------------------------------------------------
export function EmptyWithOabState() {
  const navigate = useNavigate();
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await triggerDjenIngest();
    } catch {
      // SSE vai capturar o estado atualizado
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted mb-4">
        <Search className="h-8 w-8 text-muted-foreground" />
      </div>
      <h2 className="text-xl font-semibold mb-2">
        Nenhuma intimação encontrada ainda
      </h2>
      <p className="text-muted-foreground max-w-md mb-6">
        Não localizamos publicações recentes no DJE para a sua OAB.
        Monitoramos automaticamente todos os dias — você será notificado assim que houver novidades.
      </p>
      <Button onClick={handleSync} disabled={syncing}>
        <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
        Sincronizar novamente
      </Button>

      <div className="mt-8 w-full max-w-lg">
        <p className="text-sm text-muted-foreground mb-4">Enquanto isso:</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Card
            className="cursor-pointer transition-colors hover:border-primary/40"
            onClick={() => navigate('/app/modelos')}
          >
            <CardContent className="flex items-center gap-3 p-4 pt-4">
              <FileText className="h-5 w-5 text-blue-500 shrink-0" />
              <span className="text-sm font-medium">Explorar modelos de petição</span>
            </CardContent>
          </Card>
          <Card
            className="cursor-pointer transition-colors hover:border-primary/40"
            onClick={() => navigate('/app/calendario')}
          >
            <CardContent className="flex items-center gap-3 p-4 pt-4">
              <CalendarIcon className="h-5 w-5 text-amber-500 shrink-0" />
              <span className="text-sm font-medium">Acessar agenda</span>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state: INGEST_FAILED
// ---------------------------------------------------------------------------
export function FailedState({ djenProgress }: { djenProgress: DjenProgress | null }) {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await triggerDjenIngest();
    } catch {
      // SSE vai capturar o estado atualizado
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/10 mb-4">
        <CircleAlert className="h-8 w-8 text-red-500" />
      </div>
      <h2 className="text-xl font-semibold mb-2">
        Ocorreu um erro ao buscar suas intimações
      </h2>
      <p className="text-muted-foreground max-w-md mb-2">
        Não foi possível concluir a consulta ao Diário da Justiça Eletrônico.
        Tente novamente em alguns instantes.
      </p>
      {djenProgress?.error && (
        <p className="text-sm text-red-500/80 max-w-md mb-6">
          {djenProgress.error}
        </p>
      )}
      <Button onClick={handleRetry} disabled={retrying}>
        <RefreshCw className={`mr-2 h-4 w-4 ${retrying ? 'animate-spin' : ''}`} />
        Tentar novamente
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state: EMPTY_NO_OAB
// Usuário sem OAB (entrou como não-advogado). O foco é a consulta de
// processos — peticionamento e monitoramento por OAB não se aplicam. A
// inscrição na OAB fica como caminho secundário para quem é advogado.
// ---------------------------------------------------------------------------
export function EmptyNoOabState() {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 mb-4">
        <Search className="h-8 w-8 text-primary" />
      </div>
      <h2 className="text-xl font-semibold mb-2">
        Comece consultando um processo
      </h2>
      <p className="text-muted-foreground max-w-md mb-6">
        Pesquise pelo número do processo para acompanhar movimentações e
        documentos. Você também pode organizar seus clientes e sua agenda.
      </p>
      <Button size="lg" onClick={() => navigate('/app/processos')}>
        <Search className="mr-2 h-4 w-4" />
        Consultar processo
      </Button>

      <div className="mt-8 w-full max-w-lg">
        <p className="text-sm text-muted-foreground mb-4">Ou comece por:</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Card
            className="cursor-pointer transition-colors hover:border-primary/40"
            onClick={() => navigate('/app/clientes')}
          >
            <CardContent className="flex items-center gap-3 p-4 pt-4">
              <Users className="h-5 w-5 text-blue-500 shrink-0" />
              <span className="text-sm font-medium">Cadastrar clientes</span>
            </CardContent>
          </Card>
          <Card
            className="cursor-pointer transition-colors hover:border-primary/40"
            onClick={() => navigate('/app/calendario')}
          >
            <CardContent className="flex items-center gap-3 p-4 pt-4">
              <CalendarIcon className="h-5 w-5 text-amber-500 shrink-0" />
              <span className="text-sm font-medium">Acessar agenda</span>
            </CardContent>
          </Card>
        </div>

        <p className="mt-6 text-sm text-muted-foreground">
          É advogado?{' '}
          <Button
            variant="link"
            className="h-auto p-0 text-sm"
            onClick={() => navigate('/app/configuracoes')}
          >
            Cadastre sua OAB
          </Button>{' '}
          para monitorar o Diário da Justiça automaticamente.
        </p>
      </div>
    </div>
  );
}
